import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarCheck,
  BookOpen,
  Settings,
  X,
  ChevronDown,
  ChevronRight,
  Wallet,
  ShieldCheck,
  Briefcase,
  Building2,
  FileText,
  Award,
  ChevronLeft,
  Medal,
  LogOut,
  Database,
  Users,
  Mail,
  Boxes,
  MessagesSquare,
  ExternalLink,
  GraduationCap,
  School,
} from "lucide-react";
import { DATABANK_SITE_URL } from "../../../constants/sites";
import { openDatabank } from "../../../utils/databankHandoff";
import { useMeQuery } from "../../../modules/auth/api/authApi";
import { useForumAccessQuery } from "../../../modules/forum/api/forumApi";
import { useAuth } from "../../../context/AuthContexts";

type SidebarVariant = "desktop" | "mobile";
type SidebarProps =
  | { variant: "desktop" }
  | { variant: "mobile"; open: boolean; onClose: () => void };

type MenuChild = {
  label: string;
  to: string;
  permissions?: string[];
  roles?: string[];
  requiresSeniority?: boolean;
  requiresFinancialUpToDate?: boolean;
  isProbationer?: boolean;
};

type MenuItem = {
  label: string;
  to?: string;
  /**
   * Another site altogether, rather than a route in this app.
   *
   * Rendered as a plain anchor: a react-router NavLink would try to match it
   * against the portal's own routes and never navigate. Gating works the same
   * as for any other item.
   */
  external?: string;
  /**
   * Open `external` through a function instead of letting the browser follow
   * the href — for a link whose real destination is only known at the moment it
   * is pressed. The href stays as the plain address, so copying the link or
   * opening it in a new tab still works and still goes somewhere sensible.
   */
  externalOpen?: () => void;
  icon?: React.ReactNode;
  roles?: string[];
  permissions?: string[];
  hiddenIfMember?: boolean;
  requiresMember?: boolean; // hide unless the user has a members-table record
  /**
   * Hide unless the server says this account sits on the National Policy
   * Committee. That membership is not a role anyone is granted — it follows
   * the national office held today — so there is no role name to gate on and
   * the answer has to be asked for. See /forum/me.
   */
  requiresPolicyCommittee?: boolean;
  children?: MenuChild[];
};

type MenuSection = { title?: string; items: MenuItem[] };

// ── Menu Definition ──────────────────────────────────────────────────────────
const MENU: MenuSection[] = [
  {
    title: "Core Portal",
    items: [
      {
        // Ungated on purpose: the dashboard is the portal's landing page, so
        // every role — including ones nobody remembered to enumerate here
        // (president, firm, examiner, moderator…) — must be able to get back
        // to it after switching roles.
        label: "Dashboard",
        to: "/dashboard",
        icon: <LayoutDashboard className="w-4 h-4" />,
      },
    ],
  },
  {
    title: "Finance",
    items: [
      {
        label: "Dues & Invoicing",
        icon: <Wallet className="w-4 h-4" />,
        roles: [
          "member",
          "probationer",
          "executive",
          "chairman",
          "student",
          "firm",
          "technician",
          "past president",
          "fellow",
        ],
        children: [
          { label: "My Payments", to: "/payment/my" },
          { label: "My Invoices", to: "/my-invoices" },
          { label: "Financial Status", to: "/finance/summary" },
        ],
      },
    ],
  },
  {
    title: "Professional Track",
    items: [
      {
        label: "Application",
        icon: <Award className="w-4 h-4" />,
        roles: ["student", "probationer", "graduate", "technician"],
        hiddenIfMember: true,
        children: [
          {
            label: "My Applications",
            to: "/probationer-applications",
            isProbationer: true,
            roles: ["probationer", "graduate", "technician", "student"],
          },
        ],
      },
      {
        label: "Referrals",
        icon: <Award className="w-4 h-4" />,
        roles: ["fellow", "member"],
        children: [
          {
            label: "Application Referrals",
            to: "/my-referee-request",
            requiresFinancialUpToDate: true,
          },
          {
            label: "Upgrade Referrals",
            to: "/my-upgrade-referee-request",
            requiresFinancialUpToDate: true,
          },
        ],
      },
      {
        label: "Upgrade Applications",
        icon: <Award className="w-4 h-4" />,
        requiresMember: true,
        roles: ["student", "member", "probationer", "fellow"],
        children: [
          {
            label: "My Upgrade Applications",
          //  requiresFinancialUpToDate: true,
            to: "/upgrade-applications",
          },
        ],
      },
      {
        label: "Firm Registration",
        icon: <Building2 className="w-4 h-4" />,
        requiresMember: true,
        roles: ["member", "fellow"],
        children: [
          { label: "My Firm", to: "/my-firm" },
          { label: "My Firm Applications", to: "/firm-applications" },
          { label: "Firm Partner Requests", to: "/firm-partner-requests" },
          { label: "Firm Referee Requests", to: "/firm-referee-requests" },
        ],
      },
      {
        label: "Examinations",
        icon: <FileText className="w-4 h-4" />,
        requiresMember: true,
        roles: [
          "member",
          "fellow",
          "probationer",
          "graduate",
          "technician",
        ],
        children: [
          { label: "Open Exam Calls", to: "/exams/calls" },
          { label: "My Applications", to: "/exams/calls/applications" },
        ],
      },
      {
        label: "Examiner",
        icon: <FileText className="w-4 h-4" />,
        roles: ["examiner", "committee_examination_board"],
        children: [
          { label: "My Marking", to: "/exams/examiner/marking" },
        ],
      },
      {
        label: "Membership Committee",
        icon: <ShieldCheck className="w-4 h-4" />,
        roles: ["committee_membership_committee", "committee_membership_committee.chairman"],
        children: [
          { label: "My Upgrade Reviews", to: "/committee/upgrade-reviews" },
          { label: "My Entry Reviews", to: "/committee/entry-reviews" },
          { label: "My Firm Reviews", to: "/committee/firm-reviews" },
          // "My Exam Reviews" removed 2026-09-13 — the committee no longer
          // reviews exam applications; the secretariat decides them.
          {
            label: "Committee Applications",
            to: "/committee/upgrade-applications",
            roles: ["committee_membership_committee.chairman"],
          },
          {
            label: "Entry Applications",
            to: "/committee/entry-applications",
            roles: ["committee_membership_committee.chairman"],
          },
        ],
      },
      {
        // The National Policy Committee's closed forum. Ungated by role on
        // purpose — see requiresPolicyCommittee above.
        label: "Policy Committee Forum",
        to: "/forum",
        icon: <MessagesSquare className="w-4 h-4" />,
        requiresPolicyCommittee: true,
      },
      {
        label: "Chief Examiner",
        icon: <LayoutDashboard className="w-4 h-4" />,
        roles: ["committee_examination_board.chairman"],
        children: [
          { label: "Dashboard", to: "/exams/chief" },
          { label: "Manage Examiners", to: "/exams/chief/examiners" },
        ],
      },
      {
        label: "Exam Management",
        icon: <FileText className="w-4 h-4" />,
        roles: ["committee_examination_board.chairman"],
        children: [
          { label: "Exams", to: "/exams/chief/exams" },
          { label: "Assign Courses", to: "/exams/chief/assign-courses" },
          { label: "Result Review", to: "/exams/chief/review" },
        ],
      },
      {
        label: "Cost Databank",
        icon: <Database className="w-4 h-4" />,
        // ONE LINK, no submenu.
        //
        // This was ten entries — a browse page, the analysis and eight
        // registers — which is more menu than the rest of the portal's member
        // section put together, for a thing most members open occasionally and
        // then read at length. The registers are now offered on the page
        // itself, where each can be described; the menu says only where to go.
        //
        // NO gate here either. The databank is a member benefit and the API
        // opens every read to any signed-in user; listing roles only ever hid
        // the section from ordinary members. The DUES check lives on the page,
        // as a message rather than an absence — somebody who cannot see the
        // section learns nothing, somebody told why can act.
        //
        // STRAIGHT TO THE DATABANK'S OWN SITE, not the portal's summary page.
        //
        // The databank is a site in its own right now — nine registers, the
        // indices, the estimator — and the portal page was a lobby in front of
        // it. The member arrives SIGNED IN: the press hands this session over
        // on the URL fragment, and the databank holds it for that tab only
        // (utils/databankHandoff.ts). The registers then open or stay shut
        // there on the same dues test this page applied, so the lobby cost a
        // click and decided nothing.
        //
        // The href is the plain address, so copying the link or opening it in a
        // new tab leads to the databank's front page rather than carrying a
        // token about. /databank still exists and still works; nothing links to
        // it from the menu any more.
        external: DATABANK_SITE_URL,
        externalOpen: openDatabank,
      },
      {
        label: "Logbook Registry",
        icon: <BookOpen className="w-4 h-4" />,
        requiresMember: true,
        roles: ["student", "probationer", "graduate", "technician", "member","fellow"],
        children: [
          { label: "My Logbooks", to: "/logbook", isProbationer: true },
        ],
      },
      {
        label: "Logbook Registry",
        icon: <BookOpen className="w-4 h-4" />,
        requiresMember: true,
        // "assessor" is included so that, when the sidebar collapses to a single
        // active role, an assessor viewing under their assessor role still passes
        // this parent gate and can reach the assessor-only "Logbook Assessment".
        roles: ["fellow", "member", "past president", "assessor"],
        children: [
          {
            label: "Supervision",
            to: "/supervisor",
           // requiresSeniority: true,
            requiresFinancialUpToDate: true,
          },
          {
            label: "Logbook Assessment",
            to: "/assessor/assessments",
         //   requiresSeniority: true,
            roles: ["assessor"], // only actual assessors see the assessment page
          },
          {
            label: "Supervision Requests",
            to: "/logbook/supervisor-requests",
         //  requiresSeniority: true, MOJISOLA ZAINAB FADAIRO
            requiresFinancialUpToDate: true,
          },
        ],
      },
    ],
  },
  {
    title: "Certification",
    items: [
      {
        label: "Certificates",
        icon: <Award className="w-4 h-4" />,
        roles: [
          "member",
          "executive",
          "chairman",
          "secretary",
          "fellow",
          "firm",
          "past president",
        ],
        children: [{ label: "My Certificate", to: "/certificates" }],
      },
    ],
  },

  {
    title: "Support",
    items: [
      {
        label: "Help & Support",
        icon: <FileText className="w-4 h-4" />,
        roles: [
          "member",
          "executive",
          "chairman",
          "secretary",
          "fellow",
          "firm",
          "past president",
          "probationer",
          "assessor",
          "student",
          "technician",
        ],
        children: [
          { label: "My Tickets", to: "/support/tickets" },
          { label: "New Ticket", to: "/support/tickets/new" },
        ],
      },
    ],
  },
  {
    title: "Application managements",
    items: [
      {
        label: "Application managements",
        icon: <CalendarCheck className="w-4 h-4" />,
        // Privilege-gated rather than role-gated: whoever holds the permission
        // manages entry applications, whether that is an admin or an executive
        // office such as the president. Either permission opens the section;
        // the children below decide which of the two screens is shown.
        permissions: ["application_diet.access", "application.analysis.access"],
        children: [
          {
            label: "Browse New Applications",
            to: "/probationer-applications/admin",
            permissions: ["application_diet.access"],
          },
          {
            label: "Application Analysis",
            to: "/probationer-applications/analysis",
            permissions: ["application.analysis.access"],
          },
        ],
      },
    ],
  },
  {
    title: "Events & CPD",
    items: [
      {
        label: "Events",
        icon: <CalendarCheck className="w-4 h-4" />,
        requiresMember: true,
        roles: [
          "student",
          "probationer",
          "graduate",
          "technician",
          "member",
          "executive",
          "chairman",
          "secretary",
          "fellow",
          "firm",
          "past president",
        ],
        children: [
          { label: "Browse Events", to: "/events" },
          { label: "My Registrations", to: "/my-events" },
        ],
      },
      {
        label: "CPD",
        icon: <Medal className="w-4 h-4" />,
        requiresMember: true,
        roles: [
          "student",
          "probationer",
          "member",
          "executive",
          "chairman",
          "secretary",
          "fellow",
          "firm",
          "past president",
        ],
        children: [
          { label: "My CPD Record",        to: "/my-cpd" },
          { label: "Submit External CPD",  to: "/my-cpd?tab=submissions" },
        ],
      },
    ],
  },
  {
    title: "Administration",
    items: [
      {
        /*
         * Institute-wide settings: fees, eligibility bars and the shape of the
         * running number series.
         *
         * Gated on the PRIVILEGE alone — no role beside it — so granting
         * `settings.manage` to one person is the whole grant, and holding the
         * admin role is not. Adding `roles: ["admin"]` here would hide it from
         * exactly the executive office most likely to be given it.
         *
         * Note the string: `settings.manage`, seeded by SettingsPermissionSeeder.
         * NOT `admin.settings.manage`, which appears elsewhere in this file's
         * history as a permission nothing seeds and nobody holds.
         */
        label: "Portal Settings",
        icon: <Settings className="w-4 h-4" />,
        permissions: ["settings.manage"],
        children: [
          { label: "Fees & Numbering", to: "/admin/settings", permissions: ["settings.manage"] },
        ],
      },
      {
        label: "System Config",
        icon: <Settings className="w-4 h-4" />,
        // Role OR any one of its children's privileges. Without the permission
        // list this group opened for the `admin` role alone, so moving a
        // registry in here (Accredited Institutes) would have taken it away
        // from an admin who holds the privilege but not that role — which is
        // how these registries are granted.
        roles: ["admin"],
        permissions: [
          "admin.access", "committee.access", "department.access",
          "chapter.diet.access", "chapter.access", "designation.access",
          "executive_office.access", "executive_set.access",
          "application_diet.access", "event.institute.manage",
          "national_body.access",
        ],
        children: [
          // Accredited institutes are a registry, like chapters — the other
          // thing an event can be hosted by — so it lives here rather than
          // beside the event screens.
          { label: "Accredited Institutes", to: "/admin/accredited-institutes", permissions: ["event.institute.manage"] },
          { label: "Admins",            to: "/admins",            permissions: ["admin.access"] },
          { label: "Committees",        to: "/committees",        permissions: ["committee.access"] },
          { label: "Departments",       to: "/departments",       permissions: ["department.access"] },
          { label: "Chapter Diets",     to: "/chapter/diets",     permissions: ["chapter.diet.access"] },
          { label: "Chapters",          to: "/chapter/chapters",  permissions: ["chapter.access"] },
          { label: "Designations",      to: "/designations",      permissions: ["designation.access"] },
          { label: "Executive Offices", to: "/executive-offices", permissions: ["executive_office.access"] },
          { label: "Executive Sets",    to: "/executive-sets",    permissions: ["executive_set.access"] },
          // /logbook-diets has no route — the admin screen is /application-diets,
          // so this link landed on Not Found.
          { label: "Logbook Diets",     to: "/application-diets", permissions: ["application_diet.access"] },
          { label: "National Policy Committee", to: "/admin/national-bodies", permissions: ["national_body.access"] },
          // Where an office role is given its privileges. It had no entry at
          // all — the screen existed only to whoever knew the URL.
          { label: "Roles & Privileges", to: "/access/roles", roles: ["admin", "super_admin"] },
        ],
      },
      {
        label: "Committee Management",
        icon: <Settings className="w-4 h-4" />,
        roles: ["admin"],
        children: [
          { label: "Committees", to: "/committees", permissions: ["committee.access"] },
        ],
      },
      {
        label: "Registry Control",
        icon: <ShieldCheck className="w-4 h-4" />,
        roles: ["admin"],
        children: [
          { label: "Admins",            to: "/admins",            permissions: ["admin.access"] },
          { label: "Application Diets", to: "/application-diets", permissions: ["application_diet.access"] },
        ],
      },
      {
        label: "Membership Control",
        icon: <ShieldCheck className="w-4 h-4" />,
        roles: ["admin"],
        permissions: ["membership.analysis.access"],
        children: [
          { label: "Memberships",          to: "/member-list",          permissions: ["member.access"] },
          { label: "Membership Analytics", to: "/membership-dashboard", permissions: ["membership.analysis.access"] },
        ],
      },
      {
        // One screen, so it links straight out instead of wrapping a single
        // child. Gated on the privilege alone — admins and any executive
        // office granted upgrade_applications.access, nobody else.
        label: "Upgrade Applications",
        to: "/admin/upgrade-applications",
        icon: <Award className="w-4 h-4" />,
        permissions: ["upgrade_applications.access"],
      },
      {
        // Two screens, and deliberately two: the registry is who IS registered,
        // the applications list is what is waiting on the secretariat.
        label: "Firms",
        icon: <Building2 className="w-4 h-4" />,
        // No role gate: the accounting offices hold these privileges without
        // being admins, and a role check here would hide the section from the
        // very people granted it.
        children: [
          { label: "Firm Registry", to: "/firm-registry", permissions: ["firms.access"] },
          { label: "Firm Applications", to: "/admin/firm-applications", permissions: ["firm_applications.access"] },
        ],
      },
      {
        label: "Logbook Management",
        icon: <BookOpen className="w-4 h-4" />,
        roles: ["admin"],
        children: [
          { label: "Logbooks", to: "/admin/logbooks", permissions: ["application_diet.access"] },
          { label: "Logbook Assessment", to: "/admin/logbook-assessment", permissions: ["application_diet.access"] },
          { label: "Result Approval", to: "/admin/logbook-result-approval", permissions: ["logbook_result.approve"] },
          { label: "Assessors", to: "/assessors", permissions: ["assessor.access"] },
        ],
      },
      {
        label: "Databank",
        icon: <Database className="w-4 h-4" />,
        roles: ["admin"],
        children: [
          // Analysis is reached from the Cost Databank page, not here: this
          // group is the admin CRUD, and duplicating the link would show it
          // twice. A super admin used to get "Manage Prices" inside the member
          // databank menu as well; that menu is gone, so this is now its only
          // home for everybody who has it.
          { label: "Manage Costs", to: "/admin/databank", roles: ["admin", "super_admin"] },
        ],
      },
      {
        // Each entry names the same permission its route and its API endpoints
        // require (ExamPermissionSeeder), so a link is shown only where it will
        // actually open. The three moderation boards previously all asked for
        // exam.access, which is the permission for reading exams — not for
        // moderating them.
        label: "Exams & Grading",
        icon: <FileText className="w-4 h-4" />,
        roles: ["admin", "chief_moderator"],
        children: [
          { label: "Exam Calls",         to: "/exams/admin/exam-calls",        permissions: ["exam.diet.access"] },
          { label: "Exam Applications",  to: "/exams/admin/applications",      permissions: ["exam.access"] },
          { label: "Exams",              to: "/exams/admin/exams",             permissions: ["exam.access"] },
          { label: "Exam Sessions (Timetable)", to: "/exams/admin/sessions",  permissions: ["exam.access"] },
          { label: "Exam Center Slots",  to: "/exams/admin/exam-center-slots", permissions: ["exam.slot.access"] },
          { label: "Center Management",  to: "/exams/admin/exam-centers",      permissions: ["exam.center.access"] },
          { label: "Exam Grading",       to: "/exams/admin/exam-grading",      permissions: ["exam.grade.access"] },
          { label: "Chief Moderator",    to: "/exams/chief-moderator",         permissions: ["exam.moderation.access"] },
          { label: "Moderator Board",    to: "/exams/moderator",               permissions: ["exam.moderation.access"] },
          { label: "Moderation Board",   to: "/exams/chairman/moderation",     permissions: ["exam.moderation.access"] },
        ],
      },
      {
        label: "Events & CPD",
        icon: <CalendarCheck className="w-4 h-4" />,
        // Each child on the privilege the API actually enforces for it, and the
        // section opens for any one of them. Both of these once pointed at
        // permissions that exist nowhere in the API — `cpd.access` and
        // `admin.settings.manage` are seeded by nothing and held by nobody — so
        // CPD Management was invisible to everyone but a super admin.
        // Accredited Institutes now sits under System Config with the other
        // registries.
        permissions: ["event.access", "event.cpd.access"],
        children: [
          { label: "Events",                to: "/admin/events",                permissions: ["event.access"] },
          { label: "CPD Management",        to: "/admin/cpd",                   permissions: ["event.cpd.access"] },
          // Chapters' own CPD events, awaiting approval or allocation. On the CPD
          // capability, matching its route and the API — allocating decides whose
          // records get points, which is the CPD desk's work, not the events desk's.
          { label: "Chapter Event Requests", to: "/admin/chapter-event-requests", permissions: ["event.cpd.access"] },
        ],
      },
      {
        // Final-year students' Declaration of Intent, taken on a public form
        // before the Pledge Ceremony. On its own permission rather than the
        // admin role: these are people who are not members and gave their
        // details for one ceremony, and the brief limits the register to
        // named secretariat staff. Setting up a cohort additionally needs
        // graduate.pledge.manage, which the API checks at the writes.
        label: "Graduate Pledge",
        icon: <GraduationCap className="w-4 h-4" />,
        permissions: ["graduate.pledge.access"],
        children: [
          { label: "Submissions", to: "/admin/graduate-pledge",         permissions: ["graduate.pledge.access"] },
          { label: "Cohorts",     to: "/admin/graduate-pledge/cohorts", permissions: ["graduate.pledge.access"] },
        ],
      },
      {
        // The QSRBN register of schools teaching QS — their accreditation,
        // their Head of Department and their departmental contact. A sibling
        // of the Graduate Pledge rather than a child of it: the pledge draws
        // its pilot institutions from here, but the register is the
        // Institute's own standing record and outlives any one ceremony.
        //
        // Editing (qs.institution.manage) and matching a Head of Department
        // to a member (qs.institution.hod) are separate grants the API checks
        // at the buttons, so the entry sits on read access.
        label: "QS Institutions",
        icon: <School className="w-4 h-4" />,
        permissions: ["qs.institution.access"],
        children: [
          { label: "Register", to: "/admin/qs-institutions", permissions: ["qs.institution.access"] },
        ],
      },
      {
        // Secretariat HR. The API gates every endpoint on admin|super_admin, so
        // the role gate here is the whole gate — no per-item permission exists.
        label: "HR",
        icon: <Users className="w-4 h-4" />,
        roles: ["admin"],
        children: [
          { label: "Staff",      to: "/hr/staff" },
          { label: "Attendance", to: "/hr/attendance" },
          { label: "Leave",      to: "/hr/leave" },
        ],
      },
      {
        // Secretariat store. Admin alone is not enough — the API wants a store
        // permission too, and reading it is `store.access`. Adding items
        // (store.manage) and moving stock (store.movement) are checked at the
        // buttons inside, so both screens sit on access here.
        label: "Stores",
        icon: <Boxes className="w-4 h-4" />,
        roles: ["admin"],
        permissions: ["store.access"],
        children: [
          // One way in. The store has its own nav once you are inside it —
          // nine entries here would bury the rest of the portal.
          { label: "Open the store", to: "/stores", permissions: ["store.access"] },
        ],
      },
      {
        label: "Support Queue",
        icon: <FileText className="w-4 h-4" />,
        roles: ["admin"],
        children: [
          { label: "All Tickets", to: "/admin/support/tickets",    permissions: ["support.admin.access"] },
          { label: "Categories",  to: "/admin/support/categories", permissions: ["support.category.manage"] },
        ],
      },
      {
        // Mail-out to any mix of groups. Reading the module and composing in it
        // are separate grants — an admin cleared to review past circulars does
        // not thereby get a compose screen.
        label: "Institute Mail",
        icon: <Mail className="w-4 h-4" />,
        roles: ["admin"],
        permissions: ["mail_campaigns.access"],
        children: [
          { label: "Messages", to: "/admin/mail/campaigns", permissions: ["mail_campaigns.access"] },
          { label: "Compose",  to: "/admin/mail/compose",   permissions: ["mail_campaigns.manage"] },
        ],
      },
      {
        label: "Billing Settings",
        icon: <Settings className="w-4 h-4" />,
        roles: ["admin"],
        permissions: ["dues.analysis.access"],
        children: [
          { label: "Charge Types",        to: "/admin/billing/charge-types",   permissions: ["billing.type.access"] },
          { label: "Charge Prices",       to: "/admin/billing/charge-prices",  permissions: ["billing.price.access"] },
          // /finance/analysis is the payments register — every individual
          // payment, with reverify and receipt actions. It was called "Finance
          // Analysis", which is the name the analysis screen below needs.
          { label: "Payment Records",     to: "/finance/analysis",             permissions: ["billing.access"] },
          { label: "Finance Analysis",    to: "/dues/analysis",                permissions: ["dues.analysis.access"] },
          // Each on its own privilege rather than the section-wide
          // billing.access, so opening the billing section to somebody does not
          // hand them the dues bands, the demand note and the amendment queue
          // as a side effect.
          { label: "Membership Bands",    to: "/membership-bands",             permissions: ["billing.band.access"] },
          { label: "Demand Note",         to: "/admin/billing/demand-note",    permissions: ["billing.demand_note.access"] },
          // Already the permission the API gates the amendment queue on — the
          // sidebar was simply asking for a different one, so the link showed
          // to admins whose request for it came back 403.
          { label: "Payment Edit Requests", to: "/finance/payment-requests",   permissions: ["billing.amendment.access"] },
        ],
      },
    ],
  },
  {
    title: "Chapter",
    items: [
      {
        label: "Chapter Portal",
        icon: <Briefcase className="w-4 h-4" />,
        roles: ["chapter_chairman", "chapter_secretary"],
        children: [
          // Chairman only, matching its route — the section itself is open to
          // the secretary as well.
          { label: "Chapter Register", to: "/chapter/members", roles: ["chapter_chairman"] },
          // The chapter's own CPD events: raise the request, upload the
          // attendance once it is approved, then submit it for allocation.
          { label: "Chapter Events", to: "/chapter/events" },
          { label: "Application Referrals", to: "/chapter/my-applications" },
          { label: "Upgrade Referrals", to: "/chapter/my-upgrade-applications" },
          { label: "Firm Endorsements", to: "/chapter/firm-applications" },
          { label: "Exam Endorsements", to: "/chapter/exam-applications" },
          { label: "Institution Contacts", to: "/chapter/institution-contacts" },
        ],
      },
    ],
  },
  {
    title: "Examiner Tools",
    items: [
      {
        label: "My Courses",
        icon: <BookOpen className="w-4 h-4" />,
        roles: ["examiner"],
        children: [{ label: "Assigned Courses", to: "/exams/examiner" }],
      },
    ],
  },
  {
    title: "Chief Moderator",
    items: [
      {
        label: "Moderation Management",
        icon: <ShieldCheck className="w-4 h-4" />,
        roles: ["chief_moderator"],
        children: [
          { label: "Dashboard", to: "/exams/chief-moderator" },
          {
            label: "Manage Moderators",
            to: "/exams/chief-moderator/moderators",
          },
          {
            label: "Assign Courses",
            to: "/exams/chief-moderator/assign-courses",
          },
          { label: "Review Results", to: "/exams/chief-moderator/results" },
        ],
      },
    ],
  },
  {
    title: "Moderator",
    items: [
      {
        label: "Course Moderation",
        icon: <FileText className="w-4 h-4" />,
        roles: ["moderator"],
        children: [
          { label: "Dashboard", to: "/exams/moderator" },
          { label: "Assigned Courses", to: "/exams/moderator/courses" },
        ],
      },
    ],
  },
  {
    title: "Moderation Board",
    items: [
      {
        label: "Moderation",
        icon: <ShieldCheck className="w-4 h-4" />,
        roles: ["exam_chairman"],
        children: [
          { label: "Results Moderation", to: "/exams/chairman/moderation" },
        ],
      },
    ],
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const norm = (v: any) =>
  String(v ?? "")
    .toLowerCase()
    .trim();
const normList = (arr?: any[]) => (arr || []).map(norm).filter(Boolean);

// Super admin bypasses all role and permission gates — matches backend Gate::before behaviour
const isSuperAdmin = (roles: string[]) =>
  roles.includes("super admin") || roles.includes("super_admin");

const canSeeParent = (
  roles: string[],
  hasPerm: (p: string) => boolean,
  allowedRoles?: string[],
  allowedPerms?: string[],
) => {
  if (isSuperAdmin(roles)) return true;
  const noRoleGate = !allowedRoles || allowedRoles.length === 0;
  const noPermGate = !allowedPerms || allowedPerms.length === 0;
  if (noRoleGate && noPermGate) return true; // ungated
  const roleOk = !noRoleGate && normList(allowedRoles).some((r) => roles.includes(r));
  const permOk = !noPermGate && allowedPerms!.some((p) => hasPerm(p));
  return roleOk || permOk;
};

// hasPerm is AuthContext's hasActiveRolePermission: the privileges of the role
// currently selected in the topbar, not everything the account can do.
const canSeeChild = (
  roles: string[],
  hasPerm: (p: string) => boolean,
  allowed?: string[],
  extra?: any,
  user?: any,
) => {
  const hasMemberRecord = !!(user?.member_id || user?.member?.id);
  if (extra?.isProbationer && !["probationer", "student", "technician", "graduate"].includes(user?.whois))
    return false;
  if (
    extra?.requiresSeniority &&
    (user?.years_as_member || 0) < 5 &&
    !["fellow", "past president"].includes(user?.whois)
  )
    return false;
  if (extra?.requiresFinancialUpToDate && !user?.is_financially_up_to_date)
    return false;
  if (isSuperAdmin(roles)) return true;
  // Role gate: a child may restrict itself to specific roles (e.g.
  // assessor-only items). It is matched against the ACTIVE role only — the
  // menu shows the portal of the role being worn, and the topbar's switcher is
  // how somebody with a secondary role like "assessor" reaches its screens.
  if (extra?.roles && extra.roles.length > 0) {
    if (!normList(extra.roles).some((r) => roles.includes(r))) return false;
  }
  if (!allowed || allowed.length === 0) return true;
  // ANY of the listed permissions satisfies the gate
  return allowed.some((p) => hasPerm(p));
};

const isActivePath = (pathname: string, to: string) =>
  pathname === to || pathname.startsWith(to + "/");

function filterMenu(
  menu: MenuSection[],
  roles: string[],
  hasPerm: (p: string) => boolean,
  user?: any,
) {
  /**
   * The menu is drawn for ONE role: the one selected in the topbar's role
   * switcher, which is what `roles` carries.
   *
   * Every gate below therefore asks about that role alone — including the
   * super_admin bypass, which applies only while super_admin is the role
   * being worn. Someone holding several roles sees the portal of the one they
   * have picked, and switches the chip to see another; `hasPerm` here is
   * AuthContext's hasActiveRolePermission, scoped the same way.
   */
  const normRoles = normList(roles);

  return menu
    .map((section) => {
      const items = section.items
        .map((item) => {
          const parentOk = canSeeParent(normRoles, hasPerm, item.roles, item.permissions);

          if (item.hiddenIfMember && !!(user?.member_id || user?.member?.id)) return null;
          // Hide member-only sections from users with no members-table record yet.
          if (item.requiresMember && !(user?.member_id || user?.member?.id)) return null;
          // Answered by the server, not by the role in the topbar — including
          // for super_admin, whom the backend admits to the forum too.
          if (item.requiresPolicyCommittee && !user?.__policyCommittee) return null;

          // A group with no children stands or falls on its own gate.
          if (!item.children?.length) return parentOk ? item : null;

          let children = item.children.filter((c) =>
            canSeeChild(
              normRoles,
              hasPerm,
              c.permissions,
              {
                requiresSeniority: c.requiresSeniority,
                requiresFinancialUpToDate: c.requiresFinancialUpToDate,
                isProbationer: c.isProbationer,
                roles: c.roles,
              },
              user,
            ),
          );

          /**
           * A group opens for anyone who holds a privilege inside it.
           *
           * Several groups — System Config, HR, Stores — are gated on the
           * `admin` ROLE, while the screens inside them are gated on
           * privileges the secretariat grants per person. An executive given
           * `chapter.access` therefore had the privilege and no way to reach
           * the screen: the group it lives in was closed to them by a gate
           * that asked a different question.
           *
           * So when the group's own gate says no, it still opens if a child
           * says yes ON ITS OWN — and then ONLY those children are listed. A
           * child with no gate of its own inherits the group's, which is what
           * keeps member-facing groups (whose children carry no privileges)
           * from opening to everybody.
           */
          if (!parentOk) {
            children = children.filter((c) => c.permissions?.length || c.roles?.length);
          }

          if (!children.length) return null;
          return { ...item, children };
        })
        .filter(Boolean) as MenuItem[];
      if (!items.length) return null;
      return { ...section, items };
    })
    .filter(Boolean) as MenuSection[];
}

function resolveProfilePic(pro_pic?: string | null) {
  if (!pro_pic) return null;
  if (pro_pic.startsWith("http")) return pro_pic;
  const base = "https://api.niqsng.org";
  const cleanPath = pro_pic.startsWith("/") ? pro_pic : `/${pro_pic}`;
  return `${base}/storage${cleanPath}`;
}

function initials(name?: string) {
  if (!name) return "—";
  const parts = name.trim().split(" ");
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.substring(0, 2).toUpperCase();
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function Sidebar(props: SidebarProps) {
  const { data } = useMeQuery();
  const { activeRoleName, user: authUser, hasActiveRolePermission } = useAuth();
  const SIDEBAR_STORAGE_KEY = "logbook_auth_user_v2";

  // Parse localStorage once on mount — avoids new object reference every render
  const storageUser = useMemo(() => {
    const raw = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  }, []);

  const user = useMemo(
    () => data?.user || storageUser,
    [data?.user, storageUser],
  );

  const { pathname } = useLocation();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  /**
   * The single role the menu is drawn for — the one in the topbar switcher.
   *
   * The stored value is checked against the roles actually held before it is
   * trusted. It is a string in localStorage that outlives the role itself: a
   * role can be revoked, and renaming an executive office renames its spatie
   * role outright. A stale name would otherwise match nothing and leave the
   * person with an empty sidebar, so it falls back to the first role held —
   * which is what the topbar chip shows in that case too.
   */
  const roles = useMemo(() => {
    const raw = (authUser?.roles || user?.roles || []) as any[];
    const held = normList(raw.map((r: any) => (typeof r === "string" ? r : r?.name)));
    const active = activeRoleName ? norm(activeRoleName) : null;

    if (active && (held.includes(active) || !held.length)) return [active];
    return held.length ? [held[0]] : [];
  }, [activeRoleName, authUser?.roles, user?.roles]);

  // Scoped to the selected role, so the menu is that role's portal. Guards and
  // AccessGate keep asking the unscoped hasPermission — switching the chip
  // changes what is OFFERED, not what the account is allowed to do.
  // The Policy Committee forum has no role to gate on — membership follows
  // the national office held today — so the server is asked directly. It is
  // one small request, cached by RTK Query for the life of the session.
  const { data: forumAccess } = useForumAccessQuery();

  const filteredMenu = useMemo(
    () =>
      filterMenu(MENU, roles, hasActiveRolePermission, {
        ...(user ?? {}),
        ...(authUser ?? {}),
        __policyCommittee: !!forumAccess?.allowed,
      }),
    [roles, hasActiveRolePermission, user, authUser, forumAccess?.allowed],
  );

  useEffect(() => {
    setOpenGroups((prev) => {
      let changed = false;
      const next = { ...prev };
      filteredMenu.forEach((section) => {
        section.items.forEach((item) => {
          if (!item.children?.length) return;
          if (item.children.some((c) => isActivePath(pathname, c.to))) {
            if (!next[item.label]) {
              next[item.label] = true;
              changed = true;
            }
          }
        });
      });
      return changed ? next : prev;
    });
  }, [pathname, filteredMenu]);

  const profilePic = resolveProfilePic(user?.pro_pic);

  const content = (
    <aside className="h-full w-72 bg-white flex flex-col border-r border-gray-100 shadow-xl shadow-gray-100/60">
      {/* ── Institution Header ─────────────────────────────────────────── */}
      <div className="p-4 shrink-0">
        <div className="rounded-[1.75rem] overflow-hidden shadow-sm">
          {/* User info band — dark top */}
          <div className="bg-gray-900 flex items-center gap-3 px-5 pt-5 pb-4">
            {profilePic ? (
              <img
                src={profilePic}
                alt="avatar"
                className="w-10 h-10 rounded-2xl object-cover border-2 border-white/20 shrink-0"
                onError={(e: any) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <div className="w-10 h-10 rounded-2xl bg-indigo-500 flex items-center justify-center font-black text-xs text-white shrink-0 shadow-lg shadow-indigo-900/40">
                {initials(user?.name)}
              </div>
            )}
            <div className="overflow-hidden flex-1">
              <div className="text-[9px] font-black uppercase tracking-[0.2em] text-white/40 leading-none mb-1">
                Logged in as
              </div>
              <div className="text-sm font-black text-white truncate leading-snug">
                {user?.name || "Guest User"}
              </div>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {roles.slice(0, 1).map((r) => (
                  <span
                    key={r}
                    className="text-[8px] font-black uppercase tracking-wider px-2 py-0.5 rounded-lg bg-indigo-500/30 text-indigo-200 border border-indigo-400/20"
                  >
                    {r}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Logo band — white bottom */}
          <div className="bg-white border-x border-b border-gray-100 flex flex-col items-center gap-2 px-5 pt-5 pb-5">
            <img
              src="/niqs-logo.png"
              alt="NIQS"
              className="w-32 h-32 object-contain"
            />
            <div className="text-center">
              <div className="text-[9px] font-black uppercase tracking-[0.25em] text-gray-400 leading-none mb-0.5">
                Portal
              </div>
              <div className="text-sm font-black text-gray-900 leading-none tracking-tight">
                NIQS Nigeria
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Navigation ────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-5 scrollbar-hide">
        {filteredMenu.map((section, idx) => (
          <div key={idx}>
            {section.title && (
              <div className="px-3 mb-2">
                <span className="text-[9px] font-black uppercase tracking-[0.25em] text-gray-400">
                  {section.title}
                </span>
              </div>
            )}

            <div className="space-y-0.5">
              {section.items.map((item) => {
                const isGroup = !!item.children?.length;
                const isOpen = !!openGroups[item.label];
                const anyChildActive =
                  item.children?.some((c) => isActivePath(pathname, c.to)) ??
                  false;

                if (!isGroup && item.external) {
                  return (
                    <a
                      key={item.label}
                      href={item.external}
                      target="_blank"
                      // Without `noopener` the page we open can reach back
                      // through window.opener and navigate this one.
                      rel="noopener noreferrer"
                      onClick={(event) => {
                        // A plain left click goes through externalOpen, which
                        // knows the real destination. Anything the browser
                        // handles specially — a middle click, ctrl/cmd click,
                        // "open in new tab" — is left to follow the href, so
                        // those still work and simply land on the front page.
                        if (
                          item.externalOpen &&
                          !event.metaKey &&
                          !event.ctrlKey &&
                          !event.shiftKey &&
                          !event.altKey &&
                          event.button === 0
                        ) {
                          event.preventDefault();
                          item.externalOpen();
                        }
                        if (props.variant === "mobile") props.onClose();
                      }}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-2xl transition-all text-sm font-bold text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    >
                      <span className="shrink-0 opacity-70">{item.icon}</span>
                      <span className="flex-1">{item.label}</span>
                      {/* It leaves the portal, and the menu should say so
                          before it is clicked rather than after. */}
                      <ExternalLink className="w-3.5 h-3.5 shrink-0 opacity-50" />
                    </a>
                  );
                }

                if (!isGroup && item.to) {
                  return (
                    <NavLink
                      key={item.label}
                      to={item.to}
                      end
                      onClick={() =>
                        props.variant === "mobile" && props.onClose()
                      }
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-2xl transition-all text-sm font-bold ${
                          isActive
                            ? "bg-gray-900 text-white shadow-lg shadow-gray-900/20"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        }`
                      }
                    >
                      <span className="shrink-0 opacity-70">{item.icon}</span>
                      <span>{item.label}</span>
                    </NavLink>
                  );
                }

                return (
                  <div key={item.label}>
                    <button
                      type="button"
                      onClick={() =>
                        setOpenGroups((p) => ({
                          ...p,
                          [item.label]: !p[item.label],
                        }))
                      }
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-2xl transition-all text-sm font-bold ${
                        anyChildActive && !isOpen
                          ? "text-indigo-600 bg-indigo-50"
                          : isOpen
                          ? "text-gray-900 bg-gray-50"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="shrink-0 opacity-70">{item.icon}</span>
                        <span>{item.label}</span>
                      </div>
                      <span
                        className={`transition-transform duration-200 ${
                          isOpen ? "rotate-90" : "rotate-0"
                        }`}
                      >
                        <ChevronRight className="w-3.5 h-3.5 opacity-50" />
                      </span>
                    </button>

                    {/* Children */}
                    <div
                      className={`overflow-hidden transition-all duration-200 ease-in-out ${
                        isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                      }`}
                    >
                      <div className="ml-4 mt-1 mb-1 pl-3 border-l-2 border-gray-100 space-y-0.5">
                        {item.children?.map((c) => (
                          <NavLink
                            key={c.label}
                            to={c.to}
                            onClick={() =>
                              props.variant === "mobile" && props.onClose()
                            }
                            className={({ isActive }) =>
                              `flex items-center gap-2 px-3 py-2 rounded-xl transition-all text-xs font-bold ${
                                isActive
                                  ? "bg-gray-900 text-white shadow-lg shadow-gray-900/20"
                                  : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                              }`
                            }
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full shrink-0 transition-colors ${
                                isActivePath(pathname, c.to)
                                  ? "bg-white"
                                  : "bg-gray-300"
                              }`}
                            />
                            {c.label}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <div className="p-4 shrink-0">
        <div className="bg-gray-50 rounded-2xl px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-[9px] font-black uppercase tracking-widest text-gray-400 leading-none mb-0.5">
              System
            </div>
            <div className="text-xs font-black text-gray-500">
              © {new Date().getFullYear()} NIQS Portal
            </div>
          </div>
          <div className="w-7 h-7 rounded-xl bg-gray-200/80 flex items-center justify-center">
            <LogOut className="w-3.5 h-3.5 text-gray-400" />
          </div>
        </div>
      </div>
    </aside>
  );

  if (props.variant === "desktop") return content;

  return (
    <div
      className={`fixed inset-0 z-[100] lg:hidden ${
        props.open ? "visible" : "invisible"
      }`}
    >
      <div
        onClick={props.onClose}
        className={`absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity duration-300 ${
          props.open ? "opacity-100" : "opacity-0"
        }`}
      />
      <div
        className={`absolute left-0 top-0 h-full w-72 transition-transform duration-300 ease-out ${
          props.open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {content}
      </div>
    </div>
  );
}
